import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AssetDto,
  AssetType,
  AssetVersionDto,
  CreateAssetBodyDto,
  CreateAssetVersionFromScratchBodyDto,
  ForeignRef,
  GalleryDto,
  MoveAssetVersionBodyDto,
  SectionDto,
  UpdateAssetBodyDto,
  UpdateAssetVersionBodyDto,
} from '@picflow/types';
import { BehaviorSubject, Observable, Subject, filter, lastValueFrom, map, mergeWith, withLatestFrom } from 'rxjs';
import { downloadAsset } from '../gallery/lib/download-asset';
import { sortAssetSection } from '../gallery/lib/sort-asset-section';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { FeatureFlagsService } from './feature-flags.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SectionService } from './section.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { TenantService } from './tenant.service';
import { ToastService } from './toast.service';
import { UnseenActivityService } from './unseen-activity.service';

@Injectable({
  providedIn: 'root',
})
export class AssetService {
  allAssets: Map<string, AssetDto> = new Map();
  updateList: { id: string; galleryId: string; sectionId: string; params: UpdateAssetBodyDto }[] = [];
  deleteList: { id: string; galleryId: string; sectionId: string }[] = [];
  defaultIncludes = '&include[]=is_liked';

  public _updates: Subject<string[]> = new BehaviorSubject<string[]>(undefined);
  public readonly onUpdates: Observable<string[]> = this._updates.asObservable();

  public readonly optimisticUpdates = new Subject<{ name: string; payload: { [key: string]: unknown } }>();

  public readonly updates: Observable<{
    id: string;
    action: 'updated' | 'created' | 'deleted';
    asset?: AssetDto;
    isOptimistic: boolean;
  }> = this.socketService.onSocketEvent.pipe(
    map(e => ({ ...e, isOptimistic: false })),
    mergeWith(this.optimisticUpdates.pipe(map(e => ({ ...e, isOptimistic: true })))),
    filter(e => {
      if (e?.name === 'asset.updated') return true;
      if (e?.name === 'asset.created') return true;
      if (e?.name === 'asset.deleted') return true;
    }),
    map(event => {
      const asset = event.payload.asset as AssetDto;
      return {
        id: asset.id,
        action: event.name.split('.')[1] as 'updated' | 'created' | 'deleted',
        asset,
        isOptimistic: event.isOptimistic,
      };
    })
  );

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private loader: DataLoaderService,
    private observableLoader: ObservableDataLoaderService,
    private toastService: ToastService,
    private sectionService: SectionService,
    private tenantService: TenantService,
    private galleryPasswordService: GalleryPasswordService,
    private storage: StorageService,
    private unseenActivityService: UnseenActivityService,
    private state: StateService,
    private featureFlags: FeatureFlagsService,
    private socketService: SocketService
  ) {
    if (this.featureFlags.get('assetTags')) {
      this.defaultIncludes += '&include[]=tags';
    }
  }

  private getAssetObservableInternal(
    assetId: string,
    galleryId: string,
    data: { noIncludes?: boolean; noCache?: boolean; ignore401Errors?: boolean } = {}
  ) {
    return this.observableLoader.get(`asset_${assetId}`, () => {
      let url = `${this.config.apiEndpoint}/assets/${assetId}?gallery=${galleryId}${
        data.noIncludes ? '' : this.getIncludes()
      }${this.getPassword(galleryId, true)}`;
      if (data.ignore401Errors) url += '&ignore_401_error=true';
      return this.http.get<AssetDto>(url);
    });
  }

  public getAssetObservable(
    assetId: string,
    galleryId: string,
    options: { noIncludes?: boolean; noCache?: boolean; ignore401Errors?: boolean } = {}
  ): Observable<AssetDto> {
    return this.getAssetObservableInternal(assetId, galleryId, options).pipe(
      mergeWith(
        this.updates.pipe(
          filter(e => e.id === assetId),
          withLatestFrom(this.getAssetObservableInternal(assetId, galleryId, options)),
          map(([update, asset]) => {
            return {
              ...asset,
              ...update.asset,
            };
          })
        )
      )
    );
  }

  public async getAsset(
    assetId: string,
    galleryId?: string,
    options: { noIncludes?: boolean; noCache?: boolean; ignore401Errors?: boolean } = {}
  ): Promise<AssetDto> {
    if (!options.noCache) {
      const _asset = this.allAssets.get(assetId);
      if (_asset) return _asset;
    }
    return await this.loader.get('assets_' + assetId, () =>
      this.getAssetInternal({
        assetId: assetId,
        galleryId,
        noIncludes: options.noIncludes,
        ignore401Errors: options.ignore401Errors,
      })
    );
  }

  public async getAssetCount(galleryId: string, sectionId: string) {
    return await this.loader.get('assets_count_' + galleryId + sectionId, async () => {
      const resp = await lastValueFrom(
        this.http.get<{ total_count: number }>(
          `${
            this.config.apiEndpoint
          }/assets?section=${sectionId}&gallery=${galleryId}${this.getIncludes()}${this.getPassword(
            <string>galleryId,
            true
          )}&limit=1&include=total_count`
        )
      );

      return resp.total_count;
    });
  }

  public async getAssetCountForGallery(galleryId: string): Promise<number> {
    const sections = await this.sectionService.getSections(galleryId);
    const counts = await Promise.all(
      sections.filter(s => s.object === 'asset').map(async section => this.getAssetCount(galleryId, <string>section.id))
    );
    return counts.reduce((a, b) => a + b, 0);
  }

  public async getAssets(
    sectionId: string | ForeignRef<SectionDto>,
    galleryId: string | ForeignRef<GalleryDto>,
    options: { ignore401Errors?: boolean } = {}
  ): Promise<AssetDto[]> {
    return await this.loader.get('assets_' + sectionId, () =>
      this.getAssetsInternal({
        galleryId: <string>galleryId,
        sectionId: <string>sectionId,
        options,
      })
    );
  }

  private async getAssetsInternal(data: {
    sectionId: string;
    galleryId: string;
    options: { ignore401Errors?: boolean };
  }) {
    let url = `${this.config.apiEndpoint}/assets?section=${data.sectionId}&gallery=${
      data.galleryId
    }${this.getIncludes()}${this.getPassword(<string>data.galleryId, true)}`;
    if (data.options.ignore401Errors) {
      url += '&ignore_401_error=true';
    }
    const resp = await lastValueFrom(this.http.get<{ data: AssetDto[] }>(url));
    const assets = resp.data;
    assets.sort((a, b) => a.position - b.position);
    this.onGetNewAssets(assets);
    return assets;
  }

  public async getAssetsForGallery(
    galleryId: string,
    options: { ignore401Errors?: boolean } = {}
  ): Promise<AssetDto[]> {
    const sections = await this.sectionService.getSections(galleryId, options);

    const resps = (
      await Promise.all(
        sections
          .filter(s => s.object === 'asset')
          .map(async section => {
            const assets = await this.getAssets(section.id, galleryId, options);
            return sortAssetSection(assets, section.sorting);
          })
      )
    ).flat();

    return resps;
  }

  public async deleteAsset(asset: AssetDto, isGalleryGuest = false) {
    if (!asset) return;

    // TEMP: can be removed once the backend is rolled out: https://app.asana.com/0/1201026654052703/1205584579191263/f
    if (isGalleryGuest) {
      return await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/assets/${asset.id}`));
    }

    this.deleteList.push({ id: asset.id, galleryId: <string>asset.gallery, sectionId: <string>asset.section });

    await sleep(0);
    return this.handleDeletions();
  }

  private async handleDeletions() {
    if (!this.deleteList.length) return;
    const _deleteList = this.deleteList.slice();
    this.deleteList = [];

    const params = {
      mode: 'sync',
      allSettled: true,
      operations: _deleteList.map(update => ({
        api: 'asset',
        method: 'delete',
        payload: {
          id: update.id,
        },
      })),
    };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/bulk`, params));
    this.tenantService.refreshTenant();
  }

  private async handleUpdates() {
    const _updateList = this.updateList.slice();
    this.updateList = [];
    if (_updateList.length === 1) {
      await Promise.all(
        _updateList.map(d => {
          const url = `${this.config.apiEndpoint}/assets/${d.id}${this.getPassword(d.galleryId)}`;
          return lastValueFrom(this.http.patch(url, d.params));
        })
      );
    } else {
      const galleryIds: Set<string> = new Set();
      _updateList.forEach(c => galleryIds.add(c.galleryId));
      await Promise.all(
        Array.from(galleryIds).map(async galleryId => {
          const updates = _updateList.filter(u => u.galleryId === galleryId);
          const params: { op: 'replace'; path: string; value: unknown }[] = [];

          updates.forEach(update => {
            for (const key in update.params) {
              params.push({
                op: 'replace',
                path: `/assets/${update.id}/${key}`,
                value: update.params[key],
              });
            }
          });
          const url = `${this.config.apiEndpoint}/assets${this.getPassword(galleryId)}`;
          return this.patchRequest(url, params);
        })
      );
    }
    this.removeAssets();
  }

  private async patchRequest(
    url: string,
    params: {
      op: 'replace' | 'remove';
      path: string;
      value?: unknown;
    }[]
  ) {
    try {
      return await lastValueFrom(this.http.patch(url, params));
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (!err.error?.message?.startsWidth?.(`Can't find resource "`)) throw err;
      // The asset that we are trying to update doesn't exist for some reason
      const id = err.error.message.split('"')[1];
      const index = params.findIndex(item => item.path.includes(id));
      if (index === -1) throw err;
      params.splice(index, 1);
      if (!params.length) throw err;
      return this.patchRequest(url, params);
    }
  }

  public async updateAsset(id: string, params: UpdateAssetBodyDto, galleryId: string, sectionId: string) {
    this.updateList.push({ id, params, galleryId, sectionId });
    await sleep(0);
    return this.handleUpdates();
  }

  public async likeAsset(assetId: string, galleryId: string) {
    const url = `${this.config.apiEndpoint}/assets/${assetId}/like?gallery=${galleryId}${this.getPassword(
      galleryId,
      true
    )}`;
    await lastValueFrom(this.http.post(url, {}));
    this.removeAssets();
  }

  public async unlikeAsset(assetId: string, galleryId: string) {
    const url = `${this.config.apiEndpoint}/assets/${assetId}/like?gallery=${galleryId}${this.getPassword(
      galleryId,
      true
    )}`;
    await lastValueFrom(this.http.delete(url));
    this.removeAssets();
  }

  public async postAsset(data: {
    galleryId: string;
    sectionId: string;
    contentLength: number;
    contentType: string;
    position: number;
    name: string;
    description?: string;
  }): Promise<AssetDto> {
    const params = {
      gallery: data.galleryId,
      section: data.sectionId,
      asset_name: data.name,
      visibility: 'public',
      position: data.position,
      description: data.description || undefined,
      content_length: data.contentLength,
      content_type: data.contentType,
    };
    const resp = <AssetDto>await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets`, params));
    this.removeAssets();
    return resp;
  }

  public async postVersion(
    assetId: string,
    data: {
      contentLength: number;
      contentType: string;
      name: string;
      galleryId: string;
    }
  ) {
    const params: CreateAssetVersionFromScratchBodyDto = <CreateAssetVersionFromScratchBodyDto>{
      asset: assetId,
      asset_name: data.name,
      content_length: data.contentLength,
      content_type: data.contentType,
    };

    const version = await this.calcNextVersionNumber(assetId, data.galleryId);
    params.version_name = `v${version}`;

    const resp = <AssetVersionDto>(
      await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/asset_versions`, params))
    );
    this.removeAssets();
    return resp;
  }

  public async copyAsset(assetId: string, toGalleryId: string, toSectionId: string) {
    const params: CreateAssetBodyDto = {
      copy_from: assetId,
      gallery: toGalleryId,
      section: toSectionId,
    };
    const resp = await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets`, params));
    this.removeAssets();
    return resp;
  }

  public async copyAssets(assetIds: string[], toGalleryId: string, toSectionId: string) {
    const params = {
      mode: 'sync',
      allSettled: true,
      operations: assetIds.map(assetId => ({
        api: 'asset',
        method: 'create',
        payload: {
          body: {
            copy_from: assetId,
            gallery: toGalleryId,
            section: toSectionId,
          },
        },
      })),
    };

    const resp = await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/bulk`, params));
    this.removeAssets();
    return resp;
  }

  public async makeVersionOutOfAsset(fromAssetId: string, toAssetId: string, toGalleryId: string) {
    const version = await this.calcNextVersionNumber(toAssetId, toGalleryId);
    const params: MoveAssetVersionBodyDto = {
      move_from: fromAssetId,
      move_to: toAssetId,
    };
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/asset_versions`, params));
    this.removeAssets();
    const asset = await this.getAsset(toAssetId, toGalleryId);
    await this.updateAssetVersion(asset.version_data.id, { version_name: `v${version}` });
    this.removeAssets();
    this.loader.removeFuzzy('galleries');
    return asset;
  }

  private async calcNextVersionNumber(assetId: string, galleryId: string) {
    const versions = await this.getAssetVersions(assetId, galleryId);

    versions.sort(
      (a, b) => parseInt((a.version_name || 'v1').substring(1)) - parseInt((b.version_name || 'v1').substring(1))
    );

    const version = this.state.plan?.product?.isFree
      ? 1
      : parseInt((versions.pop()?.version_name || 'v1').substring(1)) + 1;

    return version;
  }

  public async deleteAssetVersion(versionId: string) {
    const resp = await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/asset_versions/${versionId}`));
    this.removeAssets();
    this.tenantService.refreshTenant();
    return resp;
  }

  public async deleteAllAssetVersions(galleryId?: string) {
    if (galleryId) {
      await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/galleries/${galleryId}/asset_versions`));
    } else {
      await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/asset_versions`));
    }
    this.removeAssets();
    return true;
  }

  public async updateAssetVersion(versionId: string, params: UpdateAssetVersionBodyDto) {
    const resp = await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/asset_versions/${versionId}`, params));
    this.removeAssets();
    return resp;
  }

  public async handleAssetOrderChange(asset: AssetDto) {
    return this.handleSectionAssetsOrderChange(<string>asset.section, <string>asset.gallery);
  }

  public async handleSectionAssetsOrderChange(sectionId: string, galleryId: string) {
    const assets = await this.getAssets(sectionId, galleryId);
    return this.handleOrderChange(assets, <string>galleryId, <string>sectionId);
  }

  public async handleOrderChange(assets: AssetDto[], galleryId: string, sectionId: string) {
    const changes = this.calcOrderChanges(assets);
    await Promise.all(
      changes.map(change => {
        return this.updateAsset(change.id, { position: change.pos }, galleryId, sectionId);
      })
    );
    return changes;
  }

  public calcOrderChanges(assets: AssetDto[]): { id: string; pos: number }[] {
    // if there are duplicates (positions) we just send the whole set of assets to be changed
    if (assets.some(img => assets.some(i => i.position === img.position && i.id !== img.id))) {
      return assets.map((img, i) => {
        return { id: img.id, pos: i };
      });
    }

    // Otherwise we figure out the changes and send only those
    const assetsInPreviousOrder = assets.slice().sort((a, b) => a.position - b.position);
    return assets
      .map((img, i) => {
        const oldAssetInPos = assetsInPreviousOrder[i];
        if (oldAssetInPos.id !== img.id) return { id: img.id, pos: oldAssetInPos.position };
      })
      .filter(a => a);
  }

  /**
   * Not ready yet. Backend throws an error for some reason
   * @param asset
   */
  public async assetView(asset: AssetDto) {
    await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/assets/${asset.id}/view`, {}));
  }

  public async downloadAssetVersions(
    assetVersions: AssetVersionDto[],
    type: AssetType,
    watermark: boolean[],
    galleryId: string,
    size?: number
  ) {
    const toast = this.toastService.add('Downloading...', 'info', {
      duration: undefined,
      icon: '/assets/icons/download.svg',
    });
    try {
      await Promise.all([
        ...assetVersions.map((version, i) =>
          downloadAsset(version, type, size, this.config.assetsEndpoint, watermark[i])
        ),
        sleep(1000),
      ]);
    } catch {
      this.toastService.add('Download Failed. Please try again', 'error');
    }
    assetVersions.forEach(version => this.reportSingleAssetDownload(<string>version.asset, galleryId));
    this.toastService.close(toast);
  }

  public async downloadAssetVersion(
    assetVersion: AssetVersionDto,
    type: AssetType,
    galleryId: string,
    watermark: boolean,
    size?: number
  ) {
    return this.downloadAssetVersions([assetVersion], type, [watermark], galleryId, size);
  }

  public async getApprovedAssetsCount(galleryId: string): Promise<number> {
    const assets = await this.getAssetsForGallery(galleryId);
    return assets.filter(img => img.status === 'approved').length;
  }

  public async getAssetVersions(assetId: string, galleryId: string): Promise<AssetVersionDto[]> {
    return await this.loader.get('assets_versions_' + assetId, () =>
      this.getAssetVersionsInternal({
        galleryId,
        assetId,
      })
    );
  }

  private async getAssetVersionsInternal(data: { assetId: string; galleryId: string }): Promise<AssetVersionDto[]> {
    const resp = await lastValueFrom(
      this.http.get<{ data: AssetVersionDto[] }>(
        `${this.config.apiEndpoint}/asset_versions?asset=${data.assetId}${this.getPassword(data.galleryId, true)}`
      )
    );
    return resp.data;
  }

  private async reportSingleAssetDownload(assetId: string, galleryId: string) {
    const assetsDownloaded = this.storage.get('downloaded_assets') || [];
    const assetDownloaded = assetsDownloaded.includes(assetId);
    if (assetDownloaded) return; // Only log one download per session
    this.storage.set('downloaded_assets', [...assetsDownloaded, assetId], true);
    try {
      await lastValueFrom(
        this.http.post(
          `${this.config.apiEndpoint}/assets/${assetId}/downloads?ignore_401_error=true${this.getPassword(
            galleryId,
            true
          )}`,
          {}
        )
      );
    } catch {
      console.log('Ignoring assetId download reporting error');
    }
  }

  private async getAssetInternal(data: {
    assetId: string;
    galleryId: string;
    noIncludes: boolean;
    ignore401Errors?: boolean;
  }): Promise<AssetDto> {
    let url = `${this.config.apiEndpoint}/assets/${data.assetId}?gallery=${data.galleryId}${
      data.noIncludes ? '' : this.getIncludes()
    }${this.getPassword(data.galleryId, true)}`;
    if (data.ignore401Errors) url += '&ignore_401_error=true';
    const asset = await lastValueFrom(this.http.get<AssetDto>(url));
    if (!data.noIncludes && asset) this.onGetNewAssets([asset]);
    return asset;
  }

  private getPassword(galleryId: string, notFirstParam = false) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return `${notFirstParam ? '&' : '?'}password=${encodeURIComponent(password)}`;
  }

  private onGetNewAssets(assets: AssetDto[]) {
    if (!assets) return;

    assets.forEach(asset => {
      this.allAssets.set(asset.id, asset);
      if (asset['unseen_activities'] !== undefined) {
        this.state.unseenActivityCountMap.set(asset.id, asset['unseen_activities']);
        delete asset['unseen_activities'];
        this.unseenActivityService.registerAssets(asset);
      }
    });
  }

  private getIncludes() {
    let includes = this.defaultIncludes;
    if (this.state.user) includes += '&include[]=unseen_activities';
    // if (this.state.isPublic) includes += '&status[]=approved&status[]=no_status';
    return includes;
  }

  public removeAssets() {
    this.loader.removeFuzzy('assets');
    this.loader.removeFuzzy('asset_versions');
    // this.loader.removeFuzzy('galleries'); // clearing last_changed_at and asset count
    this.allAssets.clear();
  }

  public canDisplayAsset(asset: AssetDto, versionData?: AssetVersionDto) {
    if (!versionData) versionData = asset.version_data;
    if (!versionData) return false;
    if (asset.type === 'video') {
      if (!versionData.data?.['poster_url']) return false;
      if (versionData['status'] === 'in_progress') return true;
    }
    if (versionData['status'] === 'completed') return true;
    return false;
  }
}
